import { Component, computed, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { PATH_PATIENTS } from 'src/app/patients/patients.global';
import { InvalidCabinetReason } from '../guards/knightmeds-instance.guard';
import { KnightmedsInstancesService } from '../services/knightmeds-instances.service';

/**
 * Shown when knightmedsInstanceGuard won't let this laptop into the
 * Knightmeds pages because its cabinet id is missing, unrecognised, or
 * couldn't be checked.
 *
 * Inputs are bound from the query string by withComponentInputBinding()
 * (see main.ts). Deliberately not behind the guard itself -- it is where the
 * guard sends people.
 */
@Component({
  selector: 'app-invalid-cabinet-page',
  template: `
    <div class="wrapper">
      @switch (reason()) {
        @case ('missing') {
          <h2>This laptop hasn't been assigned to a cabinet yet.</h2>
          <p>
            Open the app once with the cabinet's id in the address, for example
            <code>{{ exampleUrl() }}</code>. It's remembered from then on, so
            this only has to be done when setting the laptop up.
          </p>
          <p>
            If this computer isn't attached to a cabinet, you probably want the
            <a [routerLink]="patientsPath">patient records</a> instead.
          </p>
        }
        @case ('unknown') {
          <h2>There's no cabinet named <code>{{ cabinet() }}</code>.</h2>
          <p>
            Check the spelling, or ask an administrator to add this cabinet.
          </p>
        }
        @case ('unverifiable') {
          <h2>Couldn't check whether <code>{{ cabinet() }}</code> is a valid cabinet.</h2>
          <p>
            This usually means the laptop is offline. Check the network
            connection and reload the page.
          </p>
        }
      }

      @if (knownInstanceIds().length > 0) {
        <p>Cabinets that have been set up:</p>
        <ul>
          @for (instanceId of knownInstanceIds(); track instanceId) {
            <li><code>{{ instanceId }}</code></li>
          }
        </ul>
      }

      <h3>
        Need help? Contact us at
        <a href="mailto:calvin.nursing.emr@gmail.com">calvin.nursing.emr&#64;gmail.com</a>
      </h3>
    </div>
  `,
  styles: [`
    .wrapper {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-content: center;
      padding: 2rem;
    }
    code {
      font-family: monospace;
      word-break: break-all;
    }
  `],
  standalone: true,
  imports: [
    RouterLink,
  ],
})
export class InvalidCabinetPageComponent {
  readonly patientsPath = PATH_PATIENTS;

  /** The rejected cabinet id, absent when none was configured at all */
  readonly cabinet = input<string>();
  readonly reason = input<InvalidCabinetReason>('missing');

  private readonly knownInstanceIdsSignal = signal<string[]>([]);
  readonly knownInstanceIds = computed(() => this.knownInstanceIdsSignal());

  readonly exampleUrl = computed(() => {
    const example = this.knownInstanceIds()[0] ?? 'cabinet-one';
    return `${window.location.origin}/?cabinet=${example}`;
  });

  private instancesService = inject(KnightmedsInstancesService);

  constructor() {
    // Best effort: listing the cabinets needs the same Firestore access that
    // may have just failed, so a failure here only costs the hint below.
    this.instancesService.listInstanceIds()
      .then(ids => this.knownInstanceIdsSignal.set(ids))
      .catch(() => this.knownInstanceIdsSignal.set([]));
  }
}
